import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, isDevMode, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from "@angular/material/icon";
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { SidenavV2Component } from "./components/ui/layout/sidenav-v2/sidenav-v2.component";
import { HeaderComponent } from "./components/ui/layout/header/header.component";
import { LegalConsentGateComponent } from "./components/ui/legal-consent-gate/legal-consent-gate.component";
import { VoiceAssistantOverlayComponent, VoiceOverlayService } from './core/voice';
import { TrialEndingBannerComponent } from "./components/ui/trial-ending-banner/trial-ending-banner.component";
import { ToastContainerComponent } from "./components/ui/feedback/toast/toast-container.component";
import { UndoSnackbarComponent } from "./components/ui/feedback/undo-snackbar/undo-snackbar.component";
import { AuthService } from "./core/auth/auth.service";
import { Role } from "./models/enums";
import { AgencySelectionService } from './services/agency-selection.service';
import { DesignService } from "./services/design.service";
import { MenuService } from './services/menu.service';
import { NotificationsService } from './services/notifications.service';
import { PwaUpdateService } from './services/pwa-update.service';
import { RightSidebarService } from './services/right-sidebar.service';
import { ConnectivityService } from './services/connectivity.service';
import { ShortcutsService } from './services/shortcuts.service';
import { SubscriptionFeaturesService } from './services/subscription-features.service';
import { sidebarModePref } from './services/sidebar-mode.preference';
import { ViewPreferencesService } from './services/view-preferences.service';
import { ShortcutsHelpComponent } from './components/ui/feedback/shortcuts-help/shortcuts-help.component';
import { SwipeCloseDirective } from './components/ui/layout/swipe-close/swipe-close.directive';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, MatIconModule, SidenavV2Component, HeaderComponent, TrialEndingBannerComponent, ToastContainerComponent, UndoSnackbarComponent, LegalConsentGateComponent, VoiceAssistantOverlayComponent, A11yModule, ShortcutsHelpComponent, SwipeCloseDirective],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  public supabaseStudio = environment.supabase_studio_url;
  public auth = inject(AuthService);
  public designService = inject(DesignService);
  public menuService = inject(MenuService);
  public rightSidebarService = inject(RightSidebarService);
  public voiceOverlay = inject(VoiceOverlayService);
  private notificationsService = inject(NotificationsService);
  private router = inject(Router);
  protected readonly connectivity = inject(ConnectivityService);
  private readonly shortcuts = inject(ShortcutsService);
  private readonly subscriptionFeatures = inject(SubscriptionFeaturesService);
  updateAvailable$ = this.pwaUpdateService.updateAvailable$;

  public sidebarMode: 'expanded' | 'collapsed' | 'closed' = 'expanded';
  public useSidenavV2 = localStorage.getItem('useSidenavV2') === 'true';
  public showRightSidebar = this.rightSidebarService.isOpen$;
  public rightPanelWidth = this.rightSidebarService.panelWidth$;

  /** Mobile (< 768 px, convention du repo) : le panneau droit passe en plein écran modal. */
  protected readonly isMobile = signal(window.innerWidth < 768);

  /**
   * Vrai quand on est sur la page de maintenance : le shell applicatif
   * (sidebar, header…) est masqué même pour un utilisateur connecté, afin
   * qu'un non-WA_ADMIN bloqué en maintenance voie la page comme un anonyme.
   */
  public onMaintenancePage = signal(window.location.pathname.startsWith('/maintenance'));

  /**
   * Pages « nues », sans shell même pour un utilisateur connecté : la tablette
   * d'affichage d'une salle (MOKA-243) occupe tout l'écran ; l'activation du compte
   * (`/auth/callback`, MOKA-360) aussi — sinon l'arrivée du profil fait apparaître le
   * shell, recrée le router-outlet et relance la page d'activation.
   */
  public onBarePage = signal(isBarePath(window.location.pathname));

  /**
   * Écran de salle (`/salle/…`) : jamais de bandeau « nouvelle version » (MOKA-419).
   * Recharger fait quitter le plein écran de la tablette, que le navigateur ne laisse
   * rétablir que sur un geste de l'utilisateur.
   */
  public onRoomScreen = signal(isRoomScreenPath(window.location.pathname));

  constructor(
    private pwaUpdateService: PwaUpdateService,
    private agencySelectionService: AgencySelectionService
  ) {
    if (isDevMode()) {
      console.log('🚀 Welcome to the Supabase Admin Panel! 🚀');
      console.log('🔑 You are currently in development mode 🔑');
      console.log('📦 Supabase Studio is available at:', this.supabaseStudio);
    } else {
      console.log('🔒 You are currently in production mode 🔒');
      /*console.log = function () {
      };*/
    }

    this.pwaUpdateService.checkForUpdate();

    // Ctrl/⌘ K hors d'une barre de recherche : ouvrir Koffi (MOKA-363), s'il est dans l'offre.
    this.shortcuts.setSearchFallback(() => {
      if (!this.auth.hasProfile() || this.onBarePage() || !this.subscriptionFeatures.assistantEnabled()) return false;
      this.voiceOverlay.open();
      return true;
    });

    // Retour de la connexion : toast, sauf sur les écrans nus (la tablette d'une salle gère la sienne).
    this.connectivity.reconnected$.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!this.onBarePage()) this.notificationsService.showSuccess('Connexion rétablie');
    });

    // Suit la route courante pour masquer le shell sur /maintenance
    // (l'app-root vit toute la session : pas besoin de se désabonner).
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.onMaintenancePage.set(event.urlAfterRedirects.startsWith('/maintenance'));
        this.onBarePage.set(isBarePath(event.urlAfterRedirects));
        this.onRoomScreen.set(isRoomScreenPath(event.urlAfterRedirects));
      }
    });

    // État initial de la grille avant le premier modeChange de la sidenav.
    // La persistance et les transitions responsive appartiennent à SidenavV2
    // (source de vérité unique) : ici on ne fait que LIRE, jamais écrire —
    // le localStorage ne contient que la préférence desktop, jamais 'closed'.
    const savedMode = sidebarModePref(inject(ViewPreferencesService))();
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      this.sidebarMode = 'closed';
    } else if (savedMode) {
      this.sidebarMode = savedMode;
    } else {
      this.sidebarMode = window.innerWidth > 1200 ? 'expanded' : 'collapsed';
    }
  }

  @HostListener('window:resize')
  protected onResize(): void {
    const mobile = window.innerWidth < 768;
    if (mobile !== this.isMobile()) this.isMobile.set(mobile);
  }

  /**
   * Échap : ferme d'abord l'aide des raccourcis, puis le panneau latéral droit (sauf si une
   * modale ou un menu est ouvert, ou si une saisie est en cours : cf. `shouldCloseOnEscape`).
   */
  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.shortcuts.helpOpen()) {
      keyboardEvent.preventDefault();
      this.shortcuts.closeHelp();
      return;
    }
    this.rightSidebarService.handleEscape(keyboardEvent);
  }

  async ngOnInit() {
    // Le bootstrap de session (provideSessionBootstrap) ne bloque jamais le démarrage :
    // s'il a échoué (réseau, timeout), on le signale au lieu de laisser l'utilisateur
    // devant une app déconnectée sans explication.
    if (this.auth.bootstrapFailed()) {
      this.notificationsService.showError('Impossible de charger votre profil. Veuillez recharger la page.');
    }

    await this.agencySelectionService.checkAndPromptAgencySelection();
  }

  onSidebarModeChange(mode: 'expanded' | 'collapsed' | 'closed') {
    const isMobile = window.innerWidth < 768;
    // Prevent desktop from being set to 'closed'
    if (!isMobile && mode === 'closed') {
      mode = 'collapsed';
    }
    this.sidebarMode = mode;
  }

  toggleSidenavVersion(): void {
    this.useSidenavV2 = !this.useSidenavV2;
    localStorage.setItem('useSidenavV2', String(this.useSidenavV2));
  }

  updateApplication() {
    this.pwaUpdateService.updateApplication();
  }



  protected readonly Role = Role;




}

/** Chemins affichés sans le shell de l'application (menu, en-tête). */
function isBarePath(path: string): boolean {
  return isRoomScreenPath(path) || path.startsWith('/auth/');
}

/** Écrans muraux des salles (tablette d'affichage). */
function isRoomScreenPath(path: string): boolean {
  return path.startsWith('/salle/');
}
